
import { Component, Prop, Vue } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import {
  ISolCustomerCurrency,
  ISolCustomerDenomination,
  ISolCustomerDenominationQuantity,
} from "@/entities";
import POptionBar from "@/components/look/POptionBar.vue";
import DenominationsTable from "@/components/orders/DenominationsTable.vue";

@Component({
  name: "denominations-composition",
  components: {
    DenominationsTable,
    POptionBar,
    Multiselect,
    ValidationObserver,
    ValidationProvider,
  },
})
export default class DenominationsComposition extends Vue {
  @Prop({ required: true, default: [] })
  currencies!: ISolCustomerCurrency[];
  @Prop({ required: true })
  currency!: ISolCustomerCurrency;
  @Prop({ required: true })
  denominations!: ISolCustomerDenomination[];
  @Prop({ required: true })
  denominationQuantities!: ISolCustomerDenominationQuantity[];
  @Prop({ required: true })
  isChange!: boolean;
  collapsed = true;

  denominationSelected: ISolCustomerDenomination | null = null;
  amount: number | null = null;
  quantity: number | null = null;

  get _currency(): ISolCustomerCurrency {
    return this.currency;
  }

  set _currency(value: ISolCustomerCurrency) {
    this.$emit("update:currency", value);
  }

  get denominationByAmount(): number | null {
    return this.quantity?.toString()
      ? Math.round(this.quantity * (this.denominationSelected?.value || 0) * 100) / 100
      : null;
  }

  set denominationByAmount(value: number | null) {
    this.amount = value;
  }

  get denominationQuantity(): ISolCustomerDenominationQuantity | undefined {
    return this.denominationQuantities.find(
      (d) => d.denominationId === this.denominationSelected?.id
    );
  }

  setQuantity(): void {
    if (this.denominationSelected && this.amount !== null && this.amount.toString() !== "") {
      this.quantity = Math.floor(Number(this.amount) / this.denominationSelected.value!);
    } else {
      this.quantity = null;
      this.amount = null;
    }
  }

  get denominationsGrouped(): {
    commodityTypeDescription?: string;
    denominations: ISolCustomerDenomination[];
  }[] {
    const response: {
      commodityTypeDescription?: string;
      denominations: ISolCustomerDenomination[];
    }[] = [];
    const map: Map<string | undefined, ISolCustomerDenomination[]> = new Map(
      Array.from(this.denominations, (denomination) => [denomination.commodityTypeCode, []])
    );

    this.denominations.forEach((denomination) =>
      map.get(denomination.commodityTypeCode)!.push(denomination)
    );

    const groups: ISolCustomerDenomination[][] = Array.from(map.values());

    groups.sort((a, b) => {
      if ((a[0]?.commodityTypeCode || "") > (b[0]?.commodityTypeCode || "")) {
        return 1;
      }
      return (a[0]?.commodityTypeCode || "") < (b[0]?.commodityTypeCode || "") ? -1 : 0;
    });

    groups?.forEach((group) => {
      response.push({
        commodityTypeDescription: group[0].commodityTypeDescription,
        denominations: group,
      });
    });

    return response;
  }

  resetDenominationForm(): void {
    this.denominationSelected = null;
    this.amount = null;
    this.$nextTick(() => {
      this.quantity = null;
    });
  }

  onChangeCurrency(): void {
    this.denominationQuantities.forEach(
      (denominationQuantity) => (denominationQuantity.denominationQuantity = null)
    );

    this.resetDenominationForm();
  }

  addDenomination(): void {
    this.$set(this.denominationQuantity!, "denominationQuantity", this.quantity);

    this.resetDenominationForm();
  }

  get totalAmount(): number {
    let amount = 0;
    this.denominations.forEach((denomination, i) => {
      if (
        denomination?.value &&
        this.denominationQuantities &&
        this.denominationQuantities[i]?.denominationQuantity
      ) {
        amount += denomination?.value * (this.denominationQuantities[i].denominationQuantity || 0);
      }
    });
    return amount;
  }

  get currencySymbol(): string {
    return "";
  }

  onRemoveDenominationSelected(removed: ISolCustomerDenomination): void {
    const denominationQuantity = this.denominationQuantities.find(
      (d) => d.denominationId === removed.id
    );

    denominationQuantity!.denominationQuantity = null;
  }
}
